import { useEffect, useRef } from "react";
import api from "@/lib/api";

// User is auto-logged-out after this many milliseconds of no interaction.
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
// Cadence at which we hit /auth/refresh while the user is active.
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;   // every 10 minutes
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "visibilitychange"];

/**
 * Sliding-session hook: keeps the JWT alive while the user is active,
 * and forces logout after INACTIVITY_TIMEOUT_MS of no interaction.
 *
 * @param {boolean} enabled   only run when user is authenticated
 * @param {function} onIdle   callback fired once when idle timeout elapses
 */
export default function useSessionKeepalive(enabled, onIdle) {
  const lastActivity = useRef(Date.now());
  const idleTimer = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const markActive = () => {
      lastActivity.current = Date.now();
      // Reset idle timer
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        try { onIdle && onIdle(); } catch { /* ignore */ }
      }, INACTIVITY_TIMEOUT_MS);
    };

    // Initial start
    markActive();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActive, { passive: true }));

    // Periodic silent token refresh (skip if user has been idle a long time)
    const refreshInterval = setInterval(async () => {
      const idleFor = Date.now() - lastActivity.current;
      if (idleFor > INACTIVITY_TIMEOUT_MS) return; // do not refresh — idle timeout will kick in
      try {
        const { data } = await api.post("/auth/refresh", {});
        if (data?.token) localStorage.setItem("token", data.token);
      } catch { /* silent — /auth/me will error out naturally if truly expired */ }
    }, REFRESH_INTERVAL_MS);

    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      clearInterval(refreshInterval);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActive));
    };
  }, [enabled, onIdle]);
}
